const PACKFILE_OR_INTERNAL_RE =
  /Could not read packfile|packfile may be missing|too large to read into memory|Internal error caused this command to fail/i;

const HINT_MARKER = "Mitigations (browser git limits):";

/** Shown after {@link clarifyPackfileReadFailure} — avoids repeating the “whole pack in RAM” explanation. */
const HINT_AFTER_CLARIFIED_PACK =
  "Close other tabs or try another device with more free memory for this tab; or use desktop git on this folder. Optional: shallow clone or repack elsewhere if you want a smaller pack for in-browser git.";

type ErrorWithIsoGitData = Error & { code?: string; data?: { message?: string } };

/**
 * Prefer isomorphic-git's short `data.message` over the long InternalError wrapper
 * (file-a-bug boilerplate). Then apply {@link appendPackfileBrowserHint}.
 */
export function formatGitWorkspaceError(e: unknown): string {
  if (e instanceof Error) {
    const err = e as ErrorWithIsoGitData;
    const inner = err.data?.message;
    if (err.code === "InternalError" && typeof inner === "string" && inner.length > 0) {
      return appendPackfileBrowserHint(clarifyPackfileReadFailure(inner));
    }
    return appendPackfileBrowserHint(clarifyPackfileReadFailure(e.message));
  }
  return appendPackfileBrowserHint(clarifyPackfileReadFailure(String(e)));
}

/** Put `(RangeError: …)`-style tails on their own paragraph so narrow panels do not hide them. */
function insertEngineDetailNewline(message: string): string {
  return message.replace(/(not necessarily corrupt\.)\s(\()/g, "$1\n\n$2");
}

/** Reword misleading "missing/corrupted" copy when the usual cause is browser RAM / FS read. */
function clarifyPackfileReadFailure(message: string): string {
  const m = message.trim();
  if (m.includes("Reading the full pack into this browser tab failed")) {
    return insertEngineDetailNewline(m);
  }
  const re =
    /^Could not read packfile at (.+)\. The file may be missing, corrupted, or too large to read into memory\.(.*)$/;
  const match = m.match(re);
  if (match) {
    const detail = (match[2] ?? "").trim();
    if (!detail) {
      return insertEngineDetailNewline(
        `Could not read packfile at ${match[1]}. Reading the full pack into this browser tab failed (often out-of-memory or a read error); the file on disk is not necessarily corrupt.\n\nMost often the tab could not allocate one contiguous buffer about as large as the .pack file.`,
      );
    }
    const suffix = detail.startsWith("(") ? `\n\n${detail}` : ` ${detail}`;
    return insertEngineDetailNewline(
      `Could not read packfile at ${match[1]}. Reading the full pack into this browser tab failed (often out-of-memory or a read error); the file on disk is not necessarily corrupt.${suffix}`,
    );
  }
  return m;
}

export function appendPackfileBrowserHint(message: string): string {
  const m = message.trimEnd();
  if (!PACKFILE_OR_INTERNAL_RE.test(m)) return message;
  if (m.includes(HINT_MARKER)) return message;
  const alreadyExplainsTab =
    m.includes("Reading the full pack into this browser tab failed") ||
    m.includes("not necessarily corrupt");
  if (alreadyExplainsTab) {
    return `${m}\n\n${HINT_MARKER} ${HINT_AFTER_CLARIFIED_PACK}`;
  }
  return `${m}\n\n${HINT_MARKER} the browser loads the whole pack into this tab’s RAM (the repo on disk may be fine). Retry after closing other tabs, try another browser/device with more free memory, or use desktop git on the same folder. Optional: a shallower clone or repack elsewhere reduces pack size if you want in-browser git to stay lighter.`;
}
